import React, { ReactNode } from 'react';
import { cn } from 'src/utils/Functions';

interface ChitProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'dangerous' | 'success' | 'warning' | 'nofill';
  interactive?: boolean;
  className?: string;
  'aria-label'?: string;
  role?: string;
  tabIndex?: number;
  onKeyDown?: (e: React.KeyboardEvent) => void;
}

export const Chit: React.FC<ChitProps> = ({
  children,
  onClick,
  variant = 'default',
  interactive = true,
  className,
  'aria-label': ariaLabel,
  role,
  tabIndex,
  onKeyDown
}) => {
  const baseClasses = "backdrop-blur-sm border rounded-3xl p-5 transition-all duration-200";
  
  const variantClasses = {
    default: "bg-neutral-800/80 border-white/10 hover:bg-neutral-700/80 hover:border-white/20",
    nofill: "bg-transparent border-white/10 hover:bg-neutral-700/80 hover:border-white/20",
    dangerous: "bg-red-900/10 border-red-500/30 hover:bg-red-900/20 hover:border-red-500/40",
    success: "bg-green-900/10 border-green-500/30 hover:bg-green-900/20 hover:border-green-500/40", 
    warning: "bg-yellow-900/10 border-yellow-500/30 hover:bg-yellow-900/20 hover:border-yellow-500/40"
  };

  const interactiveClasses = interactive && onClick ? "cursor-pointer" : "";

  const isClickable = interactive && onClick;
  
  return (
    <div 
      className={cn(
        baseClasses,
        variantClasses[variant],
        interactiveClasses,
        className
      )}
      onClick={onClick}
      aria-label={ariaLabel}
      role={isClickable ? role || 'button' : role}
      tabIndex={isClickable ? tabIndex ?? 0 : tabIndex}
      onKeyDown={onKeyDown}
    >
      {children}
    </div>
  );
};

// Chit content layout component
interface ChitContentProps {
  children: ReactNode;
  className?: string;
}

export const ChitContent: React.FC<ChitContentProps> = ({ children, className }) => {
  return (
    <div className={cn("flex items-center justify-between", className)}>
      {children}
    </div>
  );
};

// Chit main area (left side content)
interface ChitMainProps {
  children: ReactNode;
  className?: string;
}

export const ChitMain: React.FC<ChitMainProps> = ({ children, className }) => {
  return (
    <div className={cn("flex items-center gap-4 flex-1", className)}>
      {children}
    </div>
  );
};

// Chit aside area (right side content)
interface ChitAsideProps {
  children: ReactNode;
  className?: string;
}

export const ChitAside: React.FC<ChitAsideProps> = ({ children, className }) => {
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {children}
    </div>
  );
};

// Chit text content area
interface ChitTextProps {
  title: string;
  subtitle?: ReactNode;
  description?: ReactNode;
  className?: string;
  titleClassName?: string;
  subtitleClassName?: string;
  descriptionClassName?: string;
}

export const ChitText: React.FC<ChitTextProps> = ({
  title,
  subtitle,
  description,
  className,
  titleClassName,
  subtitleClassName,
  descriptionClassName
}) => {
  return (
    <div className={cn("flex-1", className)}>
      <h3 className={cn("text-white text-lg font-semibold mb-1", titleClassName)}>
        {title}
      </h3>
      {subtitle && (
        <p className={cn("text-white/40 text-sm mb-1", subtitleClassName)}>
          {subtitle}
        </p>
      )}
      {description && (
        <p className={cn("text-white/30 text-xs", descriptionClassName)}>
          {description}
        </p>
      )}
    </div>
  );
};